import { RadioGroup } from '@headlessui/react';
import { ArchiveBoxXMarkIcon, CalendarDaysIcon, CheckCircleIcon, PhoneXMarkIcon } from '@heroicons/react/24/outline';
import { PhoneIcon, UserGroupIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
import { Button, Datepicker, Label, Textarea, Tooltip } from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { formatDateTimeForDb, updateTimeInDate } from '../../../../../common-utils/formatters';
import type { Company } from '../../../../@types/Company';
import type { InteractionFormData } from '../../../../@types/contactInteraction';
import { ContactInteractionType } from '../../../../@types/contactInteraction';
import type { CoproUserAttribution, CoproUserAttributionResponse } from '../../../../@types/CoproUserAttribution';
import { isRentManager, isSellManager } from '../../../../@types/CoproUserAttribution';
import type { Owner } from '../../../../@types/Owner';
import { OWNER_STEPS } from '../../../../@types/Owner';
import type { User } from '../../../../@types/User';
import { useAdsModal } from '../../../../hooks/useAdsModal';
import { useReferentSendAvailability } from '../../../../hooks/useReferentSendAvailability';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { DJ_URL } from '../../../../utils/routes';
import AdsModal from '../../../common/AdsModal/AdsModal';
import Badge from '../../../common/Badge';
import Popup from '../../../common/Popup/Popup.module';
import { generateConsentAfterCallEmailContent } from '../utils/emailContentGenerator';
import { handleContactClick } from '../utils/OwnerCommunicationService';
import EmailPreview from './EmailPreview';

// Number of consecutive unsuccessful contacts of the same type needed to suggest marking as permanently unreachable
const CONSECUTIVE_ATTEMPTS_THRESHOLD = 3;

interface ToCallFormProps {
  isOpen: boolean;
  onClose: () => void;
  owner: Owner;
  onSubmit: (data: InteractionFormData) => void;
  jwt: string;
}

// Define our own FormData interface instead of extending an existing one
interface FormData {
  interactionType: ContactInteractionType;
  interactionAt: Date;  
  meetingAt: Date;      
  meetingWith: number | null;
  meetingWithType: string | null;
  comment: string | null;
}

type FormStep = 'initial' | 'response' | 'full' | 'email-suggestion';

const ToCallForm: React.FC<ToCallFormProps> = ({ 
  isOpen, 
  onClose, 
  owner, 
  onSubmit, 
  jwt,
}) => {
  const [formStep, setFormStep] = useState<FormStep>('initial');
  const [selectedParticipantType, setSelectedParticipantType] = useState<'transactionnaire' | 'gestionnaire' | null>(null);
  const [attributions, setAttributions] = useState<CoproUserAttribution[]>([]);
  const [formData, setFormData] = useState<FormData>({
    interactionType: ContactInteractionType.PHONE,
    interactionAt: new Date(),
    meetingAt: new Date(),
    meetingWith: null,
    meetingWithType: null,
    comment: null,
  });

  const [consentEmailData, setConsentEmailData] = useState<{ subject: string; content: string } | null>(null);
  const [pendingSubmitData, setPendingSubmitData] = useState<InteractionFormData | null>(null);
  const company = useSelector((state: RootState) => state.auth.company) as Company | null;
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string | null>(null);
  const { getOwnerCompany, getOwnerPrivacyPolicyLink } = useReferentSendAvailability(company?.id);

  const { handleAdsClickById, adsModalProps } = useAdsModal();

  const handleAdsClick = () => {
    const dpeId = owner.dpe?.remoteId || owner.dpe?.id;
    if (dpeId) {
      handleAdsClickById(dpeId);
    }
  };

  useEffect(() => {
    const fetchAttributions = async() => {
      try {
        const coproId = owner.dpe?.copro?.id || owner.incomingCallCopro?.id;
        if (!coproId) {
          console.error('No copro ID found for owner');
          return;
        }

        const response = await fetch(`/api/copros/${coproId}/attributions?limit=10`, JWTHeader(jwt));
        if (!response.ok) {
          throw new Error('Failed to fetch attributions');
        }
        const data: CoproUserAttributionResponse = await response.json();
        setAttributions(data.results);
      } catch (error) {
        console.error('Error fetching attributions:', error);
      }
    };

    if (jwt && (owner.dpe?.copro?.id || owner.incomingCallCopro?.id)) {
      fetchAttributions();
    }
  }, [jwt, owner.dpe?.copro?.id, owner.incomingCallCopro?.id]);

  useEffect(() => {
    const fetchPrivacyPolicyLink = async() => {
      if (!company?.siren || !jwt) {return;}
      try {
        const response = await fetch(`/api/public/dj/companies/${company.siren}/privacy-policy`, JWTHeader(jwt));
        if (response.ok) {
          const responseData = await response.json();
          if (responseData.link) {
            setPrivacyPolicyLink(`${DJ_URL}${responseData.link}`);
          }
        }
      } catch (error) {
        console.error('Error fetching privacy policy link:', error);
      }
    };
    fetchPrivacyPolicyLink();
  }, [company?.siren, jwt]);

  const handleRefused = async() => {
    try {
      const response = await fetch(`/api/owners/${owner.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step: OWNER_STEPS.REFUSED }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update owner step to refused');
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating owner step:', error);
    }
  };

  const transactionnaires = useMemo<User[]>(
    () => attributions.filter(isSellManager).map(a => a.user),
    [attributions],
  );

  const gestionnaires = useMemo<User[]>(
    () => attributions.filter(isRentManager).map(a => a.user),
    [attributions],
  );

  const parseUserId = (raw: unknown): number | null => {
    if (typeof raw === 'number') { return raw; }
    if (typeof raw === 'string') {
      const parsed = parseInt(raw, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const handleParticipantSelect = (type: 'transactionnaire' | 'gestionnaire', userId: number) => {
    setSelectedParticipantType(type);
    const meetingWithType = type === 'transactionnaire' ? 'sales-manager' : 'rent-manager';
    setFormData(prev => ({
      ...prev,
      meetingWith: userId,
      meetingWithType,
    }));
  };

  const handleParticipantTypeSelect = (type: 'transactionnaire' | 'gestionnaire') => {
    const users = type === 'transactionnaire' ? transactionnaires : gestionnaires;
    if (users.length === 1) {
      const uid = parseUserId(users[0].id);
      if (uid !== null) { handleParticipantSelect(type, uid); }
    }
  };

  const handleUnreachable = async() => {
    await handleContactClick(owner.id, ContactInteractionType.PHONE, jwt);
    onClose();
  };

  const handlePermanentlyUnreachable = async() => {
    try {
      const response = await fetch(`/api/owners/${owner.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step: OWNER_STEPS.UNABLE_TO_REACH }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update owner step to permanently unreachable');
      }
      
      // Register the contact attempt
      await handleContactClick(owner.id, ContactInteractionType.PHONE, jwt);
      
      onClose();
    } catch (error) {
      console.error('Error updating owner step:', error);
    }
  };

  const hasConsecutiveUnsuccessfulContacts = (): boolean => {
    if (!owner.contacts || owner.contacts.length < CONSECUTIVE_ATTEMPTS_THRESHOLD) {
      return false;
    }

    // Sort contacts by date, most recent first
    const sortedContacts = [...owner.contacts].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    let consecutiveCount = 1;
    let lastType = sortedContacts[0].type;
    
    // Check for interactions that would break the sequence
    const hasInteractionBetween = (startDate: string, endDate: string): boolean => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // Check all types of interactions
      const hasLeadInteraction = owner.leadInteractions?.some(interaction => {
        const interactionDate = new Date(interaction.interactionAt);
        return interactionDate > end && interactionDate < start;
      });
      
      const hasRentManagerInteraction = owner.rentManagerInteractions?.some(interaction => {
        const interactionDate = new Date(interaction.interactionAt);
        return interactionDate > end && interactionDate < start;
      });
      
      const hasSalesManagerInteraction = owner.salesManagerInteractions?.some(interaction => {
        const interactionDate = new Date(interaction.interactionAt);
        return interactionDate > end && interactionDate < start;
      });
      
      const hasQualityReferenceInteraction = owner.qualityReferenceInteractions?.some(interaction => {
        const interactionDate = new Date(interaction.interactionAt);
        return interactionDate > end && interactionDate < start;
      });
      
      return hasLeadInteraction || hasRentManagerInteraction || 
             hasSalesManagerInteraction || hasQualityReferenceInteraction;
    };

    // Check consecutive contacts
    for (let i = 1; i < sortedContacts.length; i++) {
      const currentContact = sortedContacts[i];
      
      // If same type and no interaction between them
      if (currentContact.type === lastType && 
          !hasInteractionBetween(currentContact.createdAt, sortedContacts[i - 1].createdAt)) {
        consecutiveCount++;
        if (consecutiveCount >= CONSECUTIVE_ATTEMPTS_THRESHOLD) {
          return true;
        }
      } else {
        // Reset counter if different type or interaction found
        consecutiveCount = 1;
      }
      lastType = currentContact.type;
    }

    return false;
  };

  const handleReachable = () => {
    setFormStep('response');
  };

  const handleScheduleMeeting = () => {
    // Just use a regular Date object
    const now = new Date();
    
    // Reset form data to ensure clean state when scheduling a meeting
    setFormData({
      interactionType: ContactInteractionType.PHONE,
      interactionAt: now,
      meetingAt: now,
      meetingWith: null,
      meetingWithType: null,
      comment: null,
    });
    setSelectedParticipantType(null);
    setFormStep('full');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInteractionTypeChange = (type: ContactInteractionType) => {
    setFormData(prev => ({ ...prev, interactionType: type }));
  };

  const handleDateChange = (date: Date) => {
    const currentDate = formData.meetingAt;
    // Use the utility function that returns a Date object
    const newDate = updateTimeInDate(date, currentDate.getHours(), currentDate.getMinutes());
    setFormData((prev) => ({ ...prev, meetingAt: newDate }));
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [hours, minutes] = e.target.value.split(':').map(Number);
    // Use the utility function that returns a Date object
    const newDate = updateTimeInDate(formData.meetingAt, hours, minutes);
    setFormData((prev) => ({ ...prev, meetingAt: newDate }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Format the dates to DB format ONLY when submitting to the API
    
    const finalData: InteractionFormData = {
      interactionType: formData.interactionType,
      meetingAt: formData.meetingAt.toISOString(),
      meetingWith: formData.meetingWith,
      meetingWithType: formData.meetingWithType,
      comment: formData.comment || '',
    };
    
    const resolveCollaboratorName = (): string => {
      const users = selectedParticipantType === 'transactionnaire' ? transactionnaires : gestionnaires;
      const selected = users.find(u => parseUserId(u.id) === formData.meetingWith);
      if (selected) {
        return `${selected.firstname || ''} ${selected.lastname || ''}`.trim();
      }
      return selectedParticipantType === 'transactionnaire' ? 'le transactionnaire' : 'le gestionnaire locatif';
    };

    fetch(`/api/owners/${owner.id}/quality-reference-interactions`, {
      method: 'POST',
      headers: {
        'Authorization': JWTAuthorization(jwt),
      },
      body: JSON.stringify({
        interactionType: formData.interactionType,
        interactionAt: formatDateTimeForDb(formData.interactionAt.toISOString()),
        meetingAt: formatDateTimeForDb(formData.meetingAt.toISOString()),
        meetingWith: formData.meetingWith ? formData.meetingWith.toString() : null,
        meetingWithType: formData.meetingWithType,
        comment: formData.comment || '',
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to save interaction');
        }

        if (owner.email) {
          const collaboratorName = resolveCollaboratorName();
          const serviceType = selectedParticipantType === 'transactionnaire' ? 'transaction' : 'gestion-locative';
          const emailData = generateConsentAfterCallEmailContent(
            owner,
            getOwnerCompany(owner),
            collaboratorName,
            serviceType as 'transaction' | 'gestion-locative',
            formData.interactionAt,
            getOwnerPrivacyPolicyLink(owner, privacyPolicyLink),
          );
          setConsentEmailData(emailData);
          setPendingSubmitData(finalData);
          setFormStep('email-suggestion');
        } else {
          onSubmit(finalData);
          onClose();
        }
      })
      .catch(error => {
        console.error('Error saving interaction:', error);
        onSubmit(finalData);
        onClose();
      });
  };

  const getCivilityLabel = (civility: number): string => {
    switch (civility) {
    case 1: return 'M.';
    case 2: return 'Mme';
    case 3: return 'Société';
    default: return '';
    }
  };

  const getFullName = (): string => {
    const civility = getCivilityLabel(owner.civility);
    const firstname = owner.firstname || '';
    const lastname = owner.lastname || '';
    return `${civility} ${firstname} ${lastname}`.trim();
  };

  const getFullAddress = (): string => {
    const address = owner.address || '';
    const postalCode = owner.postalCode || '';
    const city = owner.city || '';
    return `${address}${address ? ', ' : ''}${postalCode} ${city}`.trim();
  };

  const OwnerDetails: React.FC = () => (
    <>
      <div className="mb-2">
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-col space-y-2">
            <p className="font-medium">{getFullName()}</p>
            <div className="flex items-center space-x-2">
              {owner.isPotentialOwner !== undefined && owner.isPotentialOwner !== null ? (
                owner.isPotentialOwner ? (
                  <Badge mode="warning" text="Propriétaire potentiel" size="small"/>
                ) : (
                  <Badge mode="success" text="Propriétaire certain" size="small"/>
                )
              ) : (
                <Badge mode="warning" text="Statut non défini" size="small"/>
              )}
              <span className="text-sm text-gray-500">
                {owner.livingIn !== undefined && owner.livingIn !== null ? (owner.livingIn ? 'Résident' : 'Non résident') : 'Statut inconnu'}
              </span>
            </div>
            <p className="text-sm text-gray-500">{getFullAddress()}</p>
            <p className="text-sm text-gray-500">
              {owner.phone ? `Tél : ${owner.phone}` : 'Téléphone non renseigné'} 
              {owner.email ? ` | Email : ${owner.email}` : ' | Email non renseigné'}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <Label value="Détails du lot" />
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
          {(owner.lotNumber || owner.floorNumber || owner.doorNumber || owner.surface !== undefined || owner.dpe?.copro || owner.incomingCallCopro) ? (
            <div className="grid grid-cols-2 gap-4">
              {owner.lotNumber && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Lot :</span> {owner.lotNumber}
                </p>
              )}
              {owner.surface !== undefined && owner.surface !== null && owner.surface !== 0 && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Surface :</span> {owner.surface} m²
                </p>
              )}
              {owner.floorNumber !== undefined && owner.floorNumber !== null && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Étage :</span> {owner.floorNumber}
                </p>
              )}
              {owner.doorNumber !== undefined && owner.doorNumber !== null && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Porte :</span> {owner.doorNumber}
                </p>
              )}
              {(owner.dpe?.copro?.name || owner.incomingCallCopro?.name) && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Copropriété :</span> {owner.dpe?.copro?.name || owner.incomingCallCopro?.name}
                </p>
              )}
              {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) || (owner.incomingCallCopro?.addresses && owner.incomingCallCopro.addresses.length > 0) ? (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Adresse copro :</span> {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) ? String(owner.dpe.copro.addresses[0].address ?? '') : String(owner.incomingCallCopro?.addresses?.[0]?.address ?? '')}
                </p>
              ) : null}
              {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) || (owner.incomingCallCopro?.addresses && owner.incomingCallCopro.addresses.length > 0) ? (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Ville copro :</span> {(owner.dpe?.copro?.addresses && owner.dpe.copro.addresses.length > 0) ? `${owner.dpe.copro.addresses[0].postalCode} ${owner.dpe.copro.addresses[0].city}` : `${owner.incomingCallCopro?.addresses?.[0]?.postalCode || ''} ${owner.incomingCallCopro?.addresses?.[0]?.city || ''}`}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Aucune information complétée</p>
          )}
        </div>
      </div>

      <div className="mb-2">
        <div className="flex items-center justify-between">
          <Label value="DPE" />
          {owner.dpe && (
            <button
              type="button"
              onClick={handleAdsClick}
              className="cursor-pointer text-sm text-green-600 underline hover:text-green-700 focus:outline-none"
            >
              Recherche annonces
            </button>
          )}
        </div>
        <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
          {owner.dpe?.details ? (
            <div className="grid grid-cols-2 gap-4">
              {owner.dpe.details.classeBilanDpe && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Classe énergie :</span> {String(owner.dpe.details.classeBilanDpe)}
                </p>
              )}
              {owner.dpe.details.classeBilanGes && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Classe GES :</span> {String(owner.dpe.details.classeBilanGes)}
                </p>
              )}
              {owner.dpe.details.epConso5UsagesM2 && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Consommation :</span> {Math.round(Number(owner.dpe.details.epConso5UsagesM2))} kWh/m²/an
                </p>
              )}
              {owner.dpe.details.dateReceptionDpe && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Date de réception du DPE :</span> {new Date(owner.dpe.details.dateReceptionDpe.split(' ')[0]).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Aucune information complétée</p>
          )}
        </div>
      </div>
    </>
  );

  const renderInitialSelection = () => {
    const showPermanentlyUnreachableButton = hasConsecutiveUnsuccessfulContacts();
    
    return (
      <Popup 
        isOpen={isOpen} 
        onClose={onClose} 
        title={`Contact avec ${getFullName()}`}
        width="800px"
      >
        <div className="space-y-6">
          <OwnerDetails />
          <div className='mb-2'>
            <Label value="Suivi du propriétaire" />
            <div className="mt-1 flex justify-center space-x-4 lg:space-x-10">
              <div className="flex flex-col">
                <Button color="red" onClick={handleUnreachable}>
                  <PhoneXMarkIcon className="mr-2 h-5 w-5 shrink-0" />
                  Injoignable
                </Button>
                {showPermanentlyUnreachableButton && (
                  <button
                    onClick={handlePermanentlyUnreachable}
                    className="mt-2 cursor-pointer text-sm text-red-700 underline hover:text-red-800 focus:outline-none"
                    title={`${CONSECUTIVE_ATTEMPTS_THRESHOLD} tentatives consécutives sans succès`}
                  >
                    Classer définitivement injoignable
                  </button>
                )}
              </div>
              <div className="flex flex-col">
                <Button color="green" onClick={handleReachable}>
                  <CheckCircleIcon className="mr-2 h-5 w-5 shrink-0" />
                  A répondu
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  };

  const renderResponseSelection = () => (
    <Popup 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Réponse de ${getFullName()}`}
      width="800px"
    >
      <div className="space-y-6">
        <OwnerDetails />
        <div className="mb-2">
          <Label value="Suivi du propriétaire" />
          <div className="mt-1 flex justify-center space-x-4 lg:space-x-10">
            <Button color="red" onClick={handleRefused}>
              <ArchiveBoxXMarkIcon className="mr-2 h-5 w-5 shrink-0" />
              Ne souhaite plus être recontacté
            </Button>
            <Button color="green" onClick={handleScheduleMeeting}>
              <CalendarDaysIcon className="mr-2 h-5 w-5 shrink-0" />
              Souhaite prendre rendez-vous
            </Button>
          </div>
        </div>
      </div>
    </Popup>
  );

  const renderFullForm = () => (
    <Popup 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Enregistrer une prise de rdv avec ${getFullName()}`}
      width="800px"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <OwnerDetails />
          <div className="mb-2">
            <Label value="Suivi du propriétaire" />
            <br/>
            <Label value="Mise en relation avec" />
            <div className="mt-1 grid grid-cols-2 gap-2">
              {transactionnaires.length === 0 ? (
                <div className="flex w-full items-center justify-center">
                  <Tooltip 
                    content="Merci d'assigner un transactionnaire à cette copro dans les étapes de paramétrage"
                    placement="top"
                  >
                    <div className="flex w-full items-center justify-center">
                      <Button 
                        color="light"
                        disabled
                        className="w-full"
                      >
                        <div className="flex flex-col items-center">
                          <span>Transactionnaire</span>
                          <span className="text-xs">
                            (Aucun transactionnaire assigné)
                          </span>
                        </div>
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              ) : transactionnaires.length === 1 ? (
                <div className="flex w-full items-center justify-center">
                  <Button 
                    color={selectedParticipantType === 'transactionnaire' ? 'blue' : 'light'}
                    onClick={() => handleParticipantTypeSelect('transactionnaire')}
                    className="w-full"
                  >
                    <div className="flex flex-col items-center">
                      <span>Transactionnaire</span>
                      <span className="text-xs">
                        ({transactionnaires[0].firstname} {transactionnaires[0].lastname})
                      </span>
                    </div>
                  </Button>
                </div>
              ) : (
                <div className="flex w-full flex-col items-center gap-1">
                  <span className="text-sm font-medium">Transactionnaire</span>
                  <select
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${
                      selectedParticipantType === 'transactionnaire'
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-300 bg-gray-50'
                    }`}
                    value={selectedParticipantType === 'transactionnaire' && formData.meetingWith ? String(formData.meetingWith) : ''}
                    onChange={(e) => {
                      const uid = parseUserId(e.target.value);
                      if (uid !== null) { handleParticipantSelect('transactionnaire', uid); }
                    }}
                  >
                    <option value="">-- Choisir un transactionnaire --</option>
                    {transactionnaires.map(u => (
                      <option key={u.id} value={String(u.id)}>
                        {u.firstname} {u.lastname}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {gestionnaires.length === 0 ? (
                <div className="flex w-full items-center justify-center">
                  <Tooltip 
                    content="Merci d'assigner un gestionnaire locatif à cette copro dans les étapes de paramétrage"
                    placement="top"
                  >
                    <div className="flex w-full items-center justify-center"> 
                      <Button 
                        color="light"
                        disabled
                        className="w-full"
                      >
                        <div className="flex flex-col items-center">
                          <span>Gestionnaire locatif</span>
                          <span className="text-xs">
                            (Aucun gestionnaire locatif assigné)
                          </span>
                        </div>
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              ) : gestionnaires.length === 1 ? (
                <div className="flex w-full items-center justify-center">
                  <Button 
                    color={selectedParticipantType === 'gestionnaire' ? 'blue' : 'light'}
                    onClick={() => handleParticipantTypeSelect('gestionnaire')}
                    className="w-full"
                  >
                    <div className="flex flex-col items-center">
                      <span>Gestionnaire locatif</span>
                      <span className="text-xs">
                        ({gestionnaires[0].firstname} {gestionnaires[0].lastname})
                      </span>
                    </div>
                  </Button>
                </div>
              ) : (
                <div className="flex w-full flex-col items-center gap-1">
                  <span className="text-sm font-medium">Gestionnaire locatif</span>
                  <select
                    className={`w-full rounded-lg border px-3 py-2 text-sm ${
                      selectedParticipantType === 'gestionnaire'
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-300 bg-gray-50'
                    }`}
                    value={selectedParticipantType === 'gestionnaire' && formData.meetingWith ? String(formData.meetingWith) : ''}
                    onChange={(e) => {
                      const uid = parseUserId(e.target.value);
                      if (uid !== null) { handleParticipantSelect('gestionnaire', uid); }
                    }}
                  >
                    <option value="">-- Choisir un gestionnaire locatif --</option>
                    {gestionnaires.map(u => (
                      <option key={u.id} value={String(u.id)}>
                        {u.firstname} {u.lastname}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="mb-2">
            <Label value="Lieu du rendez-vous" />
            <div className="mt-1 flex w-full justify-center">
              <RadioGroup value={formData.interactionType} onChange={handleInteractionTypeChange}>
                <RadioGroup.Label className="sr-only">Lieu du rendez-vous</RadioGroup.Label>
                <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
                  <RadioGroup.Option value={ContactInteractionType.PHONE} className={({ checked }) => `${checked ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none`}>
                    <PhoneIcon className="h-5 w-5" />
                    Téléphone
                  </RadioGroup.Option>
                  <RadioGroup.Option value={ContactInteractionType.VISIO} className={({ checked }) => `${checked ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} ml-1 flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none`}>
                    <VideoCameraIcon className="h-5 w-5" />
                    Visio
                  </RadioGroup.Option>
                  <RadioGroup.Option value={ContactInteractionType.PHYSICAL} className={({ checked }) => `${checked ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} ml-1 flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none`}>
                    <UserGroupIcon className="h-5 w-5" />
                    Présentiel
                  </RadioGroup.Option>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="mb-2">
            <div className="mb-2 space-y-4">
              <div>
                <Label htmlFor="meetingAt" value="Date du rendez-vous" />
                <div className="mt-1">
                  <Datepicker
                    id="meetingAt"
                    name="meetingAt"
                    onChange={handleDateChange}
                    minDate={new Date()}
                    language="fr"
                    labelTodayButton="Aujourd'hui"
                    labelClearButton="Effacer"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="meetingTime" value="Heure du rendez-vous" />
                <div className="mt-1">
                  <input
                    type="time"
                    id="meetingTime"
                    name="meetingTime"
                    onChange={handleTimeChange}
                    value={formData.meetingAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-2">
            <Label htmlFor="comment" value="Commentaire" />
            <Textarea
              id="comment"
              name="comment"
              value={formData.comment || ''}
              onChange={handleInputChange}
              rows={6}
              className="mt-1"
              placeholder="Ajouter des notes ou commentaires sur cette interaction..."
            />
          </div>
        </div>
      </form>
      <div className="mt-6 flex w-full justify-between">
        <Button color="gray" onClick={onClose}>
          Annuler
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="blue"
          disabled={!selectedParticipantType || !formData.meetingWith}
        >
          Enregistrer le rendez-vous
        </Button>
      </div>
    </Popup>
  );

  const renderAdsModal = () => (
    <AdsModal {...adsModalProps} />
  );

  const handleEmailPreviewClose = () => {
    if (pendingSubmitData) {
      onSubmit(pendingSubmitData);
      setPendingSubmitData(null);
    }
    onClose();
  };

  const renderEmailSuggestion = () => {
    if (!consentEmailData || !owner.email) {return null;}

    return (
      <EmailPreview
        isOpen={true}
        onClose={handleEmailPreviewClose}
        emailContent={consentEmailData.content}
        emailSubject={consentEmailData.subject}
        emailTo={owner.email}
        viewOnly={false}
        templateOptions={[]}
        noCrmTracking={true}
        actionRegisteredMessage="Le rendez-vous a bien été enregistré. Nous vous suggérons d'envoyer ce mail de confirmation au copropriétaire afin de garder une trace écrite du consentement recueilli lors de votre échange téléphonique."
      />
    );
  };

  if (formStep === 'email-suggestion') {
    return (
      <>
        {renderEmailSuggestion()}
        {renderAdsModal()}
      </>
    );
  }

  if (formStep === 'initial') {
    return (
      <>
        {renderInitialSelection()}
        {renderAdsModal()}
      </>
    );
  }

  if (formStep === 'response') {
    return (
      <>
        {renderResponseSelection()}
        {renderAdsModal()}
      </>
    );
  }

  return (
    <>
      {renderFullForm()}
      {renderAdsModal()}
    </>
  );
};

export default ToCallForm; 